/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import * as constants from '../../../../common/constants';
import { normalizeScoreTextContent } from './evalExecutionReportUtils';

const unwrapResponseData = (response) => {
  if (response == null) return null;
  return response?.data ?? response;
};

const isPartialScoresResponse = (data, currentReport) =>
  Boolean(
    currentReport &&
      data &&
      typeof data === 'object' &&
      data.execution == null &&
      data.recentRuns == null &&
      (data.scores != null || data.scoresCount != null)
  );

const EvalReportData = types
  .model('EvalReportData', {
    consolidatedReport: types.maybeNull(types.frozen()),
    currentPanelScoreDetail: types.maybeNull(types.frozen()),
    pendingPanelScoreDetailRequest: types.maybeNull(types.frozen())
  })
  .actions((self) => ({
    fetchCompositeReport({
      tenantId,
      graphId,
      windowDays,
      limit,
      executionId,
      offset,
      graphVersion,
      scoresOnly,
      rubricName
    }) {
      Framework.getEventManager().publish(constants.GET_EVAL_REPORT_COMPOSITE, {
        tenantId,
        graphId,
        windowDays,
        limit,
        executionId,
        offset,
        graphVersion,
        scoresOnly: scoresOnly === true,
        rubricName
      });
    },
    fetchPanelScoreDetail({ tenantId, executionId, auditId, requestId }) {
      applySnapshot(self, {
        ...getSnapshot(self),
        pendingPanelScoreDetailRequest: { tenantId, executionId, auditId, requestId },
        currentPanelScoreDetail: null
      });
      Framework.getEventManager().publish(constants.GET_EVAL_REPORT_PANEL_SCORE_DETAIL, {
        tenantId,
        executionId,
        auditId,
        requestId
      });
    },
    setCompositeResponse(response) {
      const data = unwrapResponseData(response);
      const currentReport = self.consolidatedReport;

      if (isPartialScoresResponse(data, currentReport)) {
        applySnapshot(self, {
          ...getSnapshot(self),
          consolidatedReport: {
            ...currentReport,
            scores: data.scores ?? currentReport.scores,
            scoresCount: data.scoresCount ?? currentReport.scoresCount,
            offset: data.offset ?? currentReport.offset,
            limit: data.limit ?? currentReport.limit
          }
        });
      } else {
        applySnapshot(self, {
          ...getSnapshot(self),
          consolidatedReport: data && typeof data === 'object' ? data : null
        });
      }
      Framework.getEventManager().publish(constants.SET_EVAL_REPORT_COMPOSITE);
    },
    setPanelScoreDetailResponse(response) {
      const pending = self.pendingPanelScoreDetailRequest;
      applySnapshot(self, { ...getSnapshot(self), pendingPanelScoreDetailRequest: null });
      if (!pending) {
        return;
      }
      const data = response == null ? null : normalizeScoreTextContent(response);
      applySnapshot(self, {
        ...getSnapshot(self),
        currentPanelScoreDetail: data ? { auditId: pending.auditId, textContent: data.textContent } : null
      });
    },
    failPanelScoreDetailRequest() {
      applySnapshot(self, { ...getSnapshot(self), pendingPanelScoreDetailRequest: null });
    },
    clearAll() {
      applySnapshot(self, {
        ...getSnapshot(self),
        consolidatedReport: null,
        currentPanelScoreDetail: null,
        pendingPanelScoreDetailRequest: null
      });
    }
  }));

export default EvalReportData;
